// src/store/situationStore.js
// Situation Reconciliation Engine
//
// Single source of truth for all normalized operational state.
// All screens consume from here — never compute their own water/power/road status.
//
// Canonical state objects:
//   water  : { status, type, source, zones, details, updatedAt }
//   power  : { status, label, outages, severity, updatedAt }
//   roads  : { nearby, zoneWide, incidents, status, updatedAt }
//   alerts : { count, highest, items, updatedAt }
//   health : { score, label, color }
//   ops    : { status, label, issues }

import { create } from 'zustand';

// ── Offline utility cache ─────────────────────────────────────────
// Persists last known utility status to localStorage so it's
// available when Firestore listeners fail offline.
const UTILITY_CACHE_KEY = 'eos-utility-status-v1';
const POWER_CACHE_KEY   = 'eos-power-status-v1';
const WATER_CACHE_KEY   = 'eos-water-status-v1';

const ALERTS_CACHE_KEY  = 'eos-alerts-v1';
const ROADS_CACHE_KEY   = 'eos-roads-v1';

function saveAlertsCache(alerts) {
  try { localStorage.setItem(ALERTS_CACHE_KEY, JSON.stringify({ alerts, _cachedAt: new Date().toISOString() })); } catch {}
}
function loadAlertsCache() {
  try { const r = localStorage.getItem(ALERTS_CACHE_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
}
function saveRoadsCache(incidents) {
  try { localStorage.setItem(ROADS_CACHE_KEY, JSON.stringify({ incidents, _cachedAt: new Date().toISOString() })); } catch {}
}
function loadRoadsCache() {
  try { const r = localStorage.getItem(ROADS_CACHE_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
}

const POWER_NORMALIZED_KEY = 'eos-power-normalized-v1';
const WATER_NORMALIZED_KEY = 'eos-water-normalized-v1';

function savePowerCache(power) {
  try { localStorage.setItem(POWER_NORMALIZED_KEY, JSON.stringify({ ...power, _cachedAt: new Date().toISOString() })); } catch {}
}
function loadPowerCache() {
  try { const r = localStorage.getItem(POWER_NORMALIZED_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
}
function saveWaterNormCache(water) {
  try { localStorage.setItem(WATER_NORMALIZED_KEY, JSON.stringify({ ...water, _cachedAt: new Date().toISOString() })); } catch {}
}
function loadWaterNormCache() {
  try { const r = localStorage.getItem(WATER_NORMALIZED_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
}

function saveUtilityCache(utilityStatus) {
  try {
    localStorage.setItem(UTILITY_CACHE_KEY, JSON.stringify({
      ...utilityStatus,
      _cachedAt: new Date().toISOString(),
    }));
  } catch {}
}

function loadUtilityCache() {
  try {
    const raw = localStorage.getItem(UTILITY_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}


// ── Zone → Water Utility mapping ─────────────────────────────────
// Only show boil water advisories from the zone's actual water provider.
// SWBNO serves Orleans East Bank + West Bank only.
// Other parishes have their own water systems.
const ZONE_WATER_UTILITY = {
  // SWBNO (New Orleans Sewerage & Water Board)
  'Mid-City':          'swbno',
  'Lakeview':          'swbno',
  'Gentilly':          'swbno',
  'N.O. East':         'swbno',
  'French Quarter':    'swbno',
  'Uptown':            'swbno',
  'Carrollton':        'swbno',
  'Lower 9th Ward':    'swbno',
  'Bywater':           'swbno',
  'Tremé':             'swbno',
  // SWBNO West Bank
  'Algiers':           'swbno',
  'Gretna':            'swbno',
  'Westwego':          'swbno',
  'Marrero':           'swbno',
  'Harvey':            'swbno',
  'Belle Chasse':      'swbno',
  'Terrytown':         'swbno',
  // Jefferson Parish Water System
  'Metairie':          'jefferson_parish',
  'Kenner':            'jefferson_parish',
  'Jefferson Parish':  'jefferson_parish',
  // St. Tammany Parish Water
  'Northshore / Slidell': 'st_tammany',
  'Covington':         'st_tammany',
  'Mandeville':        'st_tammany',
  'Madisonville':      'st_tammany',
  'St. Tammany Parish':'st_tammany',
  // Livingston Parish
  'Denham Springs':    'livingston_parish',
  'Walker':            'livingston_parish',
  // Tangipahoa Parish
  'Hammond':           'tangipahoa_parish',
  'Ponchatoula':       'tangipahoa_parish',
  // St. Bernard Parish
  'Chalmette':         'st_bernard_parish',
  'Arabi':             'st_bernard_parish',
  // Plaquemines Parish
  'Plaquemines Parish':'plaquemines_parish',
  // St. Charles Parish
  'St. Charles Parish':'st_charles_parish',
  'Destrehan':         'st_charles_parish',
  'Luling':            'st_charles_parish',
  // St. John Parish
  'St. John Parish':   'st_john_parish',
  'LaPlace':           'st_john_parish',
  'Reserve':           'st_john_parish',
  // Baton Rouge / EBR
  'Baton Rouge':       'baton_rouge_water',
  'Prairieville':      'baton_rouge_water',
  // Default — unknown
};

// Only SWBNO boil water data is currently scraped.
// Other utilities: show unknown until their data is integrated.
const SWBNO_ZONES = new Set(
  Object.entries(ZONE_WATER_UTILITY)
    .filter(([,v]) => v === 'swbno')
    .map(([k]) => k)
);

// ── Source health enums ───────────────────────────────────────────
export const SOURCE_STATUS = {
  LIVE:        'live',        // Data received within freshness threshold
  STALE:       'stale',       // Data older than threshold but still available
  UNAVAILABLE: 'unavailable', // Source not responding / fetch failed
  CACHED:      'cached',      // Showing last known good data
  SIMULATED:   'simulated',   // Simulator override active
  UNKNOWN:     'unknown',     // No data received yet
};

// ── Status enums ──────────────────────────────────────────────────
export const STATUS = {
  NORMAL:    'normal',
  MINOR:     'minor',
  DEGRADED:  'degraded',
  DISRUPTED: 'disrupted',
  UNKNOWN:   'unknown',
  STALE:     'stale',
};

// ── Normalizers ───────────────────────────────────────────────────
function normalizeWater(utilityStatus, zone) {
  if (!utilityStatus) return { status: STATUS.UNKNOWN, label: 'Unknown', source: null, advisory: false };

  // Only apply SWBNO boil water to zones actually served by SWBNO
  const zoneUtility = ZONE_WATER_UTILITY[zone] ?? 'unknown';
  const isSwbnoZone = SWBNO_ZONES.has(zone);

  const swbnoAdvisory = isSwbnoZone && !!(
    utilityStatus.boilWaterActive &&
    (utilityStatus.boilWaterZones?.includes(zone) ||
     utilityStatus.boilWaterParishes?.some(p =>
       zone?.toLowerCase().includes(p.toLowerCase()) ||
       p.toLowerCase().includes(zone?.toLowerCase().split(' ')[0])
     ))
  );

  const advisory = swbnoAdvisory || !!(utilityStatus.boilWaterAdvisory || utilityStatus.waterAdvisory);
  const source = isSwbnoZone ? 'SWBNO' : zoneUtility.replace(/_/g,' ').replace(/\w/g,l=>l.toUpperCase());

  // For non-SWBNO zones, water status is unknown until their utility is integrated
  const waterStatus = !isSwbnoZone
    ? STATUS.UNKNOWN
    : advisory ? STATUS.DEGRADED : STATUS.NORMAL;
  const waterLabel = !isSwbnoZone
    ? `Unknown (${source})`
    : advisory ? 'Boil advisory' : 'No known advisory';

  return {
    status:    waterStatus,
    label:     waterLabel,
    advisory,
    source,
    utility:   zoneUtility,
    details:   utilityStatus.boilWaterDetails ?? null,
    updatedAt: utilityStatus.fetchedAt ?? null,
  };
}

// Zone → Entergy parish name mapping (uppercase to match byParish keys)
const ZONE_TO_ENTERGY_PARISH = {
  'Lakeview': 'ORLEANS', 'Mid-City': 'ORLEANS', 'Uptown': 'ORLEANS',
  'French Quarter': 'ORLEANS', 'Gentilly': 'ORLEANS', 'Algiers': 'ORLEANS',
  'Carrollton': 'ORLEANS', 'N.O. East': 'ORLEANS', 'Lower 9th Ward': 'ORLEANS',
  'New Orleans': 'ORLEANS', 'Bywater': 'ORLEANS', 'Marigny': 'ORLEANS',
  'Metairie': 'JEFFERSON', 'Kenner': 'JEFFERSON', 'Jefferson': 'JEFFERSON',
  'Gretna': 'JEFFERSON', 'Harvey': 'JEFFERSON', 'Marrero': 'JEFFERSON',
  'Westwego': 'JEFFERSON', 'Elmwood': 'JEFFERSON',
  'Chalmette': 'ST. BERNARD', 'Arabi': 'ST. BERNARD',
  'Covington': 'ST. TAMMANY', 'Mandeville': 'ST. TAMMANY', 'Slidell': 'ST. TAMMANY',
  'Northshore / Slidell': 'ST. TAMMANY',
  'Belle Chasse': 'PLAQUEMINES', 'Buras': 'PLAQUEMINES',
  'Hahnville': 'ST. CHARLES', 'Luling': 'ST. CHARLES', 'Destrehan': 'ST. CHARLES',
  'LaPlace': 'ST. JOHN THE BAPTIST', 'Reserve': 'ST. JOHN THE BAPTIST',
  'Hammond': 'TANGIPAHOA', 'Ponchatoula': 'TANGIPAHOA',
  'Houma': 'TERREBONNE', 'Thibodaux': 'LAFOURCHE',
  'Gonzales': 'ASCENSION', 'Denham Springs': 'LIVINGSTON',
};

function normalizePower(utilityStatus, zone) {
  if (!utilityStatus) return { status: STATUS.UNKNOWN, label: 'Unknown', outages: 0 };
  // Use parish-specific outage count when available
  const parishKey = ZONE_TO_ENTERGY_PARISH[zone] ?? null;
  const parishData = parishKey ? utilityStatus.byParish?.[parishKey] : null;
  const outages = parishData?.customersAffected
    ?? utilityStatus.parishAffected
    ?? utilityStatus.customersAffected ?? 0;
  let status, label;
  if (outages === 0) {
    status = STATUS.NORMAL; label = 'No known outages';
  } else if (outages < 100) {
    status = STATUS.MINOR; label = `Minor disruption · ${outages} affected`;
  } else if (outages < 5000) {
    status = STATUS.DEGRADED; label = `${outages} without power`;
  } else {
    status = STATUS.DISRUPTED; label = `Major disruption · ${outages} affected`;
  }
  return { status, label, outages, byParish: utilityStatus.byParish ?? null, updatedAt: utilityStatus.fetchedAt ?? null };
}

function _haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 +
    Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

const ZONE_CENTERS = {
  'St. Rose':       { lat: 29.960, lon: -90.310 },
  'Destrehan':      { lat: 29.942, lon: -90.358 },
  'Luling':         { lat: 29.901, lon: -90.369 },
  'Norco':          { lat: 29.986, lon: -90.408 },
  'Hahnville':      { lat: 29.965, lon: -90.413 },
  'N.O. East':      { lat: 30.024, lon: -89.954 },
  'Lakeview':       { lat: 30.001, lon: -90.115 },
  'Gentilly':       { lat: 30.005, lon: -90.070 },
  'Mid-City':       { lat: 29.974, lon: -90.087 },
  'Uptown':         { lat: 29.929, lon: -90.105 },
  'French Quarter': { lat: 29.957, lon: -90.063 },
  'Algiers':        { lat: 29.915, lon: -90.032 },
};
const NEARBY_KM = 25; // incidents within 25km of zone center count as local

function normalizeRoads(incidents, radius = 1, zone = null) {
  if (!incidents?.length) return {
    status: STATUS.UNKNOWN, nearby: 0, zoneWide: 0, incidents: [], label: 'Unknown'
  };
  const dotd = incidents.filter(i => i.source === 'dotd_511la' || i.type === 'road' || i.type === 'road_closure');
  const zoneCenter = zone ? ZONE_CENTERS[zone] : null;

  // Filter to geographically relevant incidents when zone center is known
  const local = zoneCenter
    ? dotd.filter(i => {
        const iLat = i.lat ?? parseFloat(i.StartLatitude ?? i.Latitude ?? 0);
        const iLon = i.lon ?? parseFloat(i.StartLongitude ?? i.Longitude ?? 0);
        if (!iLat || !iLon) return true;
        return _haversineKm(zoneCenter.lat, zoneCenter.lon, iLat, iLon) <= NEARBY_KM;
      })
    : dotd;

  const zoneWide = local.length;
  const nearby = zoneWide;
  let status, label;
  if (zoneWide === 0) {
    status = STATUS.NORMAL; label = 'No known closures';
  } else if (zoneWide < 5) {
    status = STATUS.MINOR; label = `${zoneWide} known closure${zoneWide!==1?'s':''}`;
  } else {
    status = STATUS.DEGRADED; label = `${zoneWide} known closures`;
  }
  return { status, label, nearby, zoneWide, incidents: local };
}

function normalizeAlerts(alerts) {
  if (!alerts?.length) return { count: 0, highest: null, status: STATUS.NORMAL };
  const bySeverity = { extreme: 4, severe: 3, moderate: 2, minor: 1 };
  const highest = alerts.reduce((h, a) =>
    (bySeverity[a.severity?.toLowerCase()] ?? 0) > (bySeverity[h?.severity?.toLowerCase()] ?? 0) ? a : h
  , alerts[0]);
  return { count: alerts.length, highest, status: STATUS.DEGRADED };
}


// ── Assessment versions ───────────────────────────────────────────
// Bump when scoring logic changes so historical results are traceable.
const HEALTH_ASSESSMENT_VERSION = 'health-v1.2';
const OPS_ASSESSMENT_VERSION    = 'ops-v1.1';

function computeHealth(water, power, roads, alerts) {
  let score = 100;
  if (alerts.count > 0) score -= Math.min(alerts.count * 8, 40);
  if (water.advisory) score -= 20;
  if (power.status === STATUS.MINOR) score -= 8;
  if (power.status === STATUS.DEGRADED) score -= 15;
  if (power.status === STATUS.DISRUPTED) score -= 25;
  if (roads.status === STATUS.MINOR) score -= 8;
  if (roads.status === STATUS.DEGRADED) score -= 15;
  score = Math.max(0, Math.min(100, Math.round(score)));
  const label = score >= 80 ? 'Good' : score >= 60 ? 'Some Services Disrupted' : score >= 40 ? 'Degraded' : 'Critical';
  const color = score >= 80 ? '#059669' : score >= 60 ? '#d97706' : score >= 40 ? '#dc2626' : '#7f1d1d';
  return { score, label, color, assessmentVersion: HEALTH_ASSESSMENT_VERSION };
}

function computeOps(water, power, roads) {
  const issues = [
    water.status !== STATUS.NORMAL && water.status !== STATUS.UNKNOWN,
    power.status !== STATUS.NORMAL && power.status !== STATUS.UNKNOWN,
    roads.status !== STATUS.NORMAL && roads.status !== STATUS.UNKNOWN,
  ].filter(Boolean).length;
  const status = issues === 0 ? STATUS.NORMAL : issues <= 2 ? STATUS.DEGRADED : STATUS.DISRUPTED;
  const label  = status === STATUS.NORMAL ? 'Operational' : status === STATUS.DEGRADED ? 'Degraded' : 'Disrupted';
  return { status, label, issues, assessmentVersion: OPS_ASSESSMENT_VERSION };
}


// ── Freshness thresholds (ms) ─────────────────────────────────────
const FRESHNESS = {
  nws:       5  * 60 * 1000,   // 5 min — alerts change fast
  dotd:      10 * 60 * 1000,   // 10 min — road incidents
  entergy:   10 * 60 * 1000,   // 10 min — outage data
  swbno:     30 * 60 * 1000,   // 30 min — water advisory
  community: 60 * 60 * 1000,   // 1 hour — community reports
};

function checkFreshness(updatedAt, source) {
  if (!updatedAt) return 'unknown';
  const ms = Date.now() - Date.parse(updatedAt);
  if (isNaN(ms)) return 'unknown';
  const threshold = FRESHNESS[source] ?? 15 * 60 * 1000;
  if (ms < threshold) return 'fresh';
  if (ms < threshold * 3) return 'stale';
  return 'expired';
}

// ── Contradiction detector ────────────────────────────────────────
function detectConflicts(water, power, alerts) {
  const conflicts = [];

  // Water advisory + water normal
  if (water.advisory && water.status === STATUS.NORMAL) {
    conflicts.push('water_advisory_vs_normal');
  }

  // Power outages > 0 + power normal
  if (power.outages > 0 && power.status === STATUS.NORMAL) {
    conflicts.push('power_outages_vs_normal');
  }

  // Extreme alerts + low health score would be caught by health calc
  // Log conflicts for admin visibility
  if (conflicts.length > 0) {
    console.warn('[SituationStore] State conflicts detected:', conflicts);
  }
  return conflicts;
}

// ── State transition logger ───────────────────────────────────────
function logTransition(log, field, from, to, source, { causes = [], version = null } = {}) {
  if (from === to) return log;
  const entry = {
    ts:      new Date().toISOString(),
    field,
    from,
    to,
    source:  source ?? 'system',
    causes,           // e.g. ['boil_water_advisory', '32_power_affected']
    version,          // e.g. 'health-v1.2'
  };
  return [entry, ...log].slice(0, 20); // keep last 20
}

// ── Store ─────────────────────────────────────────────────────────
export const useSituationStore = create((set, get) => ({
  // Raw inputs
  _utilityStatus: loadUtilityCache() ?? null,  // seed from localStorage
  _incidents:     [],
  _alerts:        [],
  _zone:          '',
  // Meta
  conflicts:      [],
  freshness:      { utility:'unknown', roads:'unknown', alerts:'unknown' },
  sourceHealth: {
    dotd:    { status:'unknown', lastSuccess: null, staleSince: null },
    nws:     { status:'unknown', lastSuccess: null, staleSince: null },
    entergy: { status:'unknown', lastSuccess: null, staleSince: null },
    swbno:   { status:'unknown', lastSuccess: null, staleSince: null },
  },

  // Normalized outputs
  water:  { status: STATUS.UNKNOWN, label: 'Unknown', advisory: false },
  power:  loadPowerCache() ?? { status: STATUS.UNKNOWN, label: 'Unknown', outages: 0 },
  roads:  { status: STATUS.UNKNOWN, label: 'Unknown', nearby: 0, zoneWide: 0, incidents: [] },
  alerts: { count: 0, highest: null, status: STATUS.NORMAL },
  health: { score: 100, label: 'Good', color: '#059669' },
  ops:    { status: STATUS.NORMAL, label: 'Operational', issues: 0 },
  // State transition log — last 20 transitions, newest first
  transitionLog: [],

  // Update actions — call these when Firestore data arrives
  setUtilityStatus(utilityStatus) {
    // Cache to localStorage for offline access
    if (utilityStatus) saveUtilityCache(utilityStatus);
    const zone = get()._zone;
    const water  = normalizeWater(utilityStatus, zone);
    const power  = normalizePower(utilityStatus, zone);
    const roads  = get().roads;
    const alerts = get().alerts;
    const health = computeHealth(water, power, roads, alerts);
    const ops    = computeOps(water, power, roads);
    const conflicts = detectConflicts(water, power, alerts);
    const utilityFreshness = checkFreshness(utilityStatus?.fetchedAt, 'entergy');
    const entStatus = utilityFreshness === 'fresh' ? 'live' : utilityFreshness === 'stale' ? 'stale' : 'unavailable';
    const prev = get().freshness;
    get().setSourceHealth('entergy', entStatus);
    savePowerCache(power);
    saveWaterNormCache(water);
    set({ _utilityStatus: utilityStatus, water, power, health, ops, conflicts, freshness: { ...prev, utility: utilityFreshness } });
  },

  setIncidents(incidents) {
    if (incidents?.length) saveRoadsCache(incidents);
    const roads  = normalizeRoads(incidents, 1, get()._zone);
    const water  = get().water;
    const power  = get().power;
    const alerts = get().alerts;
    const health = computeHealth(water, power, roads, alerts);
    const ops    = computeOps(water, power, roads);
    const roadFreshness = incidents.length > 0 ? checkFreshness(incidents[0]?.ingestedAt, 'dotd') : 'unknown';
    const dotdStatus = incidents.length > 0 ? (roadFreshness === 'fresh' ? 'live' : roadFreshness === 'stale' ? 'stale' : 'unavailable') : 'unknown';
    const prev = get().freshness;
    get().setSourceHealth('dotd', dotdStatus);
    const prevState1 = get();
    const log1 = [
      ...logTransition(prevState1.transitionLog ?? [], 'health', prevState1.health?.label, health.label, 'setIncidents',
        { causes: roads.status !== STATUS.NORMAL && roads.status !== STATUS.UNKNOWN ? ['ROADS_' + roads.status.toUpperCase()] : [], version: HEALTH_ASSESSMENT_VERSION }),
      ...logTransition([], 'ops', prevState1.ops?.status, ops.status, 'setIncidents',
        { causes: roads.status !== STATUS.NORMAL && roads.status !== STATUS.UNKNOWN ? ['ROADS_' + roads.status.toUpperCase()] : [], version: OPS_ASSESSMENT_VERSION }),
    ].slice(0, 20);
    set({ _incidents: incidents, roads, health, ops, freshness: { ...prev, roads: roadFreshness }, transitionLog: log1 });
  },

  setAlerts(alerts) {
    const normalized = normalizeAlerts(alerts);
    const water  = get().water;
    const power  = get().power;
    const roads  = get().roads;
    const health = computeHealth(water, power, roads, normalized);
    // Use fetchedAt — when pollNWSAlerts last polled, not when NWS originally issued the alert
    const _alertFetch = alerts[0]?.fetchedAt ?? alerts[0]?.updatedAt ?? alerts[0]?.ingestedAt;
    const alertFreshness = alerts.length > 0 ? checkFreshness(_alertFetch, 'nws') : 'unknown';
    const nwsStatus = alerts.length > 0 ? (alertFreshness === 'fresh' ? 'live' : alertFreshness === 'stale' ? 'stale' : 'unavailable') : 'unknown';
    const prev = get().freshness;
    get().setSourceHealth('nws', nwsStatus);
    const prevState2 = get();
    const alertCauses = normalized.count > 0 ? ['NWS_ACTIVE_ALERTS'] : [];
    const log2 = [
      ...logTransition(prevState2.transitionLog ?? [], 'health', prevState2.health?.label, health.label, 'setAlerts',
        { causes: alertCauses, version: HEALTH_ASSESSMENT_VERSION }),
    ].slice(0, 20);
    set({ _alerts: alerts, alerts: normalized, health, freshness: { ...prev, alerts: alertFreshness }, transitionLog: log2 });
  },

  setSourceHealth(source, status, timestamp = null) {
    const prev = get().sourceHealth;
    const now = new Date().toISOString();
    const prevSrc = prev[source] ?? { status:'unknown', lastSuccess:null, staleSince:null };
    const isLive = status === 'live';
    const wasDegraded = prevSrc.status !== 'live';
    set({ sourceHealth: { ...prev, [source]: {
      status,
      lastSuccess: isLive ? now : prevSrc.lastSuccess,
      staleSince:  isLive ? null : (wasDegraded ? prevSrc.staleSince : now),
    }}});
  },

  setZone(zone) {
    const util = get()._utilityStatus;
    const water = normalizeWater(util, zone);
    const power = get().power;
    const roads = get().roads;
    const alerts = get().alerts;
    const health = computeHealth(water, power, roads, alerts);
    const ops    = computeOps(water, power, roads);
    set({ _zone: zone, water, health, ops });
  },
}));

// ── Canonical selectors ───────────────────────────────────────────
// All safety-relevant UI state must come through these.
// No component should read raw store fields or compute derived state independently.

// Normalized outage count — use this everywhere instead of reading raw fields
export const selectOutageCount = (s) =>
  s.power?.outages ?? 0;

export const selectActiveRoadCount = (s) =>
  Math.max(s.roads?.zoneWide ?? 0, s.roads?.nearby ?? 0);

export const selectActiveRoadIncidents = (s) =>
  s.roads?.incidents ?? [];

export const selectEvacuationState = (s) => {
  const highest = s.alerts?.highest;
  if (!highest) return { active: false, alert: null };
  const isEvac = /evacuat/i.test(highest.event ?? '');
  return { active: isEvac, alert: isEvac ? highest : null };
};

export const selectCurrentPublicSafetyAlert = (s) => {
  const highest = s.alerts?.highest;
  if (!highest) return null;
  const isSafety = ['safety','hazmat','evacuation','amber'].some(t =>
    (highest.event ?? '').toLowerCase().includes(t)
  );
  return isSafety ? highest : null;
};

export const selectCurrentWeatherAlert = (s) => {
  const highest = s.alerts?.highest;
  if (!highest) return null;
  const isWeather = ['warning','watch','advisory','statement'].some(t =>
    (highest.event ?? '').toLowerCase().includes(t)
  );
  return isWeather ? highest : null;
};

export const selectCurrentSituationSeverity = (s) => ({
  score:  s.health?.score ?? 100,
  label:  s.health?.label ?? 'Good',
  color:  s.health?.color ?? '#059669',
  status: s.ops?.status ?? STATUS.NORMAL,
});

export const selectSourceHealth = (source) => (s) =>
  s.sourceHealth?.[source] ?? { status: 'unknown', lastSuccess: null, staleSince: null };

export const selectFreshness = (s) => s.freshness;

export const selectTransitionLog = (s) => s.transitionLog ?? [];

